/**
 * New Year mug designs — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_seasons`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const newYearMugDesignsPack: ToilePack = {
    slug: 'new-year-mug-designs',
    category: 'season',
    name: 'New Year mug designs',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 1024, h: 1024},
    readings: [
        {key: 'slogan', label: 'Slogan', subject: 'The words "NEW YEAR, SAME ME" lettered in a rounded script with a small star, isolated flat artwork, no mug'},
        {key: 'clock', label: 'Clock', subject: 'A clock face at midnight with a burst behind it, isolated flat artwork, no mug'},
        {key: 'band', label: 'Pattern band', subject: 'A horizontal band of small stars and confetti sized to wrap a mug, isolated flat artwork, no mug'},
    ],
    styles: [
        {key: 'deco', label: 'Art deco', style: 'art deco illustration, geometric fans and sunbursts, clean lines, no outlines', palette: 'black and gold with a little cream', framing: 'Centered on a solid black background with an even margin'},
        {key: 'neon', label: 'Neon party', style: 'neon party graphic, glowing lines, confetti and streamers', palette: 'electric pink, cyan and gold on deep navy', framing: 'Centered on a dark background, built to read clean at small size'},
        {key: 'minimal', label: 'Minimal', style: 'modern minimal illustration, few flat shapes, generous space', palette: 'gold and white with a single black', framing: 'Centered on a plain white background with generous space around it'},
    ],
    directions: ['for a coffee mug set', 'in metallic gold', 'as a wraparound pattern'],
    directionHint: 'try: for a coffee mug set',
    seo: {
        title: 'New Year Mug Design: Slogan, Clock, Pattern Band',
        metaDescription: 'Nine new year mug designs — new year new me slogan, clock, pattern band — flat artwork ready to wrap, three styles.',
        h1: 'New year mug design: slogan, clock, pattern band',
        tagline: 'Nine wraps, three midnight subjects, three styles each.',
        intro: 'Flat wrap-around artwork for the January mug: a new year new me slogan, a clock, a pattern band, each sized to sit around a standard mug without a mockup getting in the way. Ask for a coffee-and-confetti scene, a countdown numeral, or your own line, and the same three styles carry it.',
        useCases: [
            'a print-on-demand mug listing',
            "a New Year's gift mug",
            'an office or classroom giveaway',
            'a coffee shop seasonal mug run',
        ],
        faq: [
            {q: 'What does this pack actually look like?', a: 'Nine mug-wrap designs — a slogan, a clock, a pattern band — flat artwork with no mug rendered, in three styles.'},
            {q: 'Do I get a mockup with the mug shown, or just the flat design?', a: "Just the flat design — no mug is rendered, so it fits your own mockup tool or print supplier's template."},
            {q: 'Will the pattern wrap correctly around a mug without gaps?', a: 'The artwork is built as a repeating or edge-to-edge band; check the seam against your specific mug template before final print.'},
            {q: 'Can I change the slogan text?', a: 'The pack renders the wording as shown; swapping in your own line is a quick edit after downloading.'},
            {q: 'Can I sell mugs printed with this design?', a: 'Yes — check the license terms on your account for the specifics of commercial use.'},
        ],
    },
}
